import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import { Link } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { FC, useEffect } from "react";
import { RelatedCompetencySkills } from "./components/RelatedCompetencySkills";
import { initialValuesITER, validationSchemaITER } from "./constants";
import { useRequirementCreateAndEdit } from "./useRequirementCreateAndEdit";
import { REQUIREMENT } from "helpers/constants";
import { TagManager } from "components/TagManager";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";

type ITERCreateAndEditProps = {
  edit?: boolean;
};

export const ITERCreateAndEdit: FC<ITERCreateAndEditProps> = ({
  edit = false,
}) => {
  const {
    competencySkills,
    setCompetencySkills,
    isOrgView,
    handleCreateRequirement,
    handleEditRequirement,
    dataRequirement,
    tags,
    handleAddTag,
    handleDeleteTag,
  } = useRequirementCreateAndEdit(REQUIREMENT.REQUIREMENT_ITER, edit);

  const formik = useFormik({
    initialValues: initialValuesITER,
    validationSchema: validationSchemaITER,
    onSubmit: (values) => {
      if (edit) {
        handleEditRequirement(values);
      } else {
        handleCreateRequirement(values);
      }
    },
  });

  // Fill Formik values with requirement data to Edit
  useEffect(() => {
    if (dataRequirement?.requirement_by_pk && edit) {
      formik.setValues({
        name: dataRequirement.requirement_by_pk.name || "",
        description: dataRequirement.requirement_by_pk.description ?? "",
        min_small_animals_count: 0,
        min_large_animals_count: 0,
        min_performance: 0,
        hour: 0,
        minutes: 0,
        min_score: 0,
      });
    }
  }, [dataRequirement, edit]);

  return (
    <MainLayoutContainer
      title={`${edit ? "Edit" : "Create"} ITER`}
      breadcrumb={
        isOrgView
          ? BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_ITERS
          : BREADCRUMB_PAGE.ITER_LIST
      }
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ maxWidth: 960 }}>
            <Box mb={2}>
              <Typography variant="h5">Requirement Overview</Typography>
              <Divider />
            </Box>

            <Stack spacing={1}>
              <Box>
                <TextField
                  size="small"
                  id="name"
                  name="name"
                  label="Requirement Name"
                  fullWidth
                  margin="dense"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && !!formik.errors.name}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Box>

              <Box>
                <TextField
                  size="small"
                  id="description"
                  name="description"
                  label="Requirement Description"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={5}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description && !!formik.errors.description
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Box>
            </Stack>

            <Box my={2}>
              <Typography variant="h5">Competencies</Typography>
              <Divider />
            </Box>

            <Box mt={2}>
              <RelatedCompetencySkills
                label="Competencies"
                competencySkills={competencySkills}
                onChange={(newCompetencySkills) =>
                  setCompetencySkills(newCompetencySkills)
                }
              />
            </Box>

            <Box my={2}>
              <Typography variant="h5">Tags</Typography>
              <Divider />
            </Box>

            <Box>
              <TagManager
                tags={tags}
                onAdd={handleAddTag}
                onDelete={handleDeleteTag}
                isEditing={true}
              />
            </Box>

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button variant="contained" type="submit">
                  Save Changes
                </Button>
                <Button variant="outlined" component={Link} to="/iters">
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Box>
        </form>
      </FormikProvider>
    </MainLayoutContainer>
  );
};

